// EventEditorModal — pt89U (Feb 20, 2026, Randy).
//
// Extracted from CalendarSection.jsx during the Feb-20 refactor pass so
// the parent file drops from ~2000 lines to a manageable size before
// Stage-C of the AI Re-Themer sweep begins. Zero behavior change from
// the pre-extraction version.
//
// This modal handles the "New calendar item" and "Edit calendar item"
// flows for every kind — event, task (chore), routine, ping, reward.
// It hosts the assignee picker, DOW selectors, reminder chips, and the
// ping-gallery overlay with the Nudge input + retention rules (pt89T).
//
// Contract with the parent:
//   • siloId  — current silo id (needed for POST/PATCH endpoints).
//   • editing — pass the event doc when editing; null/undefined for New.
//   • defaults{} — { kind, date, time, dow, freq, question, openGallery }.
//                  When `openGallery` is true the modal auto-opens the
//                  ping suggestion gallery on mount (Organizer-Pings
//                  section uses this to jump straight into the picker).
//   • members[] — the current silo's household roster. Feeds the
//                 assignee picker AND the notify-scope picker.
//   • onClose() — parent dismisses.
//   • onSaved() — parent reloads its calendar data.

import { useEffect, useState } from "react";
import { X, Trash, Sparkle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser } from "@/lib/api";
import { KIND_META, REMINDER_OPTS, DOW_MIN, isoLocal, fmt12 } from "@/lib/calendarConstants";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";
import SiblingPropagateModal from "@/components/SiblingPropagateModal";

export default function EventEditorModal({ siloId, editing, defaults = {}, members = [], onClose, onSaved }) {
  // pt89U (Feb 20, 2026, Randy) — Stage-C theme wrap. Kind picker chips
  // now render themed labels so a re-themed silo can call tasks
  // "Assignments", routines "Rituals", pings "Nudges", etc. Reading up
  // here once means the picker's Object.entries loop can drop into a
  // simple map lookup instead of re-resolving per render.
  const kindLabels = {
    event:   useThemeLabel(T.THEME_KEY_CALENDAR_EVENT_SINGULAR),
    task:    useThemeLabel(T.THEME_KEY_CALENDAR_TASK_SINGULAR),
    routine: useThemeLabel(T.THEME_KEY_CALENDAR_ROUTINE_SINGULAR),
    ping:    useThemeLabel(T.THEME_KEY_CALENDAR_PING_SINGULAR),
  };
  // pt89U Stage-C — assignee picker labels (emojis stay universal per
  // Randy's rule; only the human-word half of each option is themable).
  const tAssignLabel   = useThemeLabel(T.THEME_KEY_EVENT_ASSIGN_LABEL);
  const tAssignEveryone = useThemeLabel(T.THEME_KEY_EVENT_ASSIGN_EVERYONE);
  const tAssignJustMe   = useThemeLabel(T.THEME_KEY_EVENT_ASSIGN_JUST_ME);
  const tAssignParent   = useThemeLabel(T.THEME_KEY_EVENT_ASSIGN_ROLE_PARENT);
  const tAssignKid      = useThemeLabel(T.THEME_KEY_EVENT_ASSIGN_ROLE_KID);
  const tAssignOwner    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_OWNER);
  const [kind, setKind] = useState(editing?.kind || defaults.kind || "event");
  const [title, setTitle] = useState(editing?.title || defaults.title || "");
  const [pingQ, setPingQ] = useState(editing?.ping_question || defaults.question || "");
  const [date, setDate] = useState(editing?.start_date || defaults.date || isoLocal(new Date()));
  const [time, setTime] = useState(editing?.start_time || defaults.time || "");
  const [endTime, setEndTime] = useState(editing?.end_time || "");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [freq, setFreq] = useState(editing?.recurrence?.freq || defaults.freq || "none");
  const [dow, setDow] = useState(editing?.recurrence?.dow || defaults.dow || []);
  const [until, setUntil] = useState(editing?.recurrence?.until || "");
  const [reminders, setReminders] = useState(editing?.reminders || defaults.reminders || []);
  const [assignee, setAssignee] = useState(editing?.assignee_user_id || "");
  const [notifyScope, setNotifyScope] = useState(editing?.notify_scope || "me");
  const [busy, setBusy] = useState(false);
  // Feb 24, 2026 (Randy pt89c-xxii Phase 0) — sibling-propagate handoff.
  // After a successful edit, snapshot the before/after event dicts so
  // SiblingPropagateModal can compute the delta and offer propagation.
  const [propagateOpen, setPropagateOpen] = useState(false);
  const [priorEvent, setPriorEvent] = useState(null);
  const [newEvent, setNewEvent] = useState(null);
  const [gallery, setGallery] = useState(null); // null | {list, busy}
  // Feb 2026 (Randy) — surface "has any promoted parent" so the recipient
  // toggle can conditionally show the "All parents" option. Members list
  // includes is_parent flags for each row.
  const hasParents = (members || []).some((m) => m.is_parent && !m.is_owner);

  // Routines/pings default to weekly-or-daily recurrence.
  useEffect(() => {
    if (!editing && (kind === "routine" || kind === "ping") && freq === "none") setFreq(dow.length ? "weekly" : "daily");
    // eslint-disable-next-line
  }, [kind]);
  // Organizer-pings section: "Browse suggestions" opens straight into the gallery.
  useEffect(() => {
    if (!editing && defaults.openGallery) openGallery();
    // eslint-disable-next-line
  }, []);

  const toggleDow = (d) => setDow((c) => (c.includes(d) ? c.filter((x) => x !== d) : [...c, d]));
  const toggleReminder = (v) => setReminders((c) => (c.includes(v) ? c.filter((x) => x !== v) : [...c, v]));

  // Feb 2026 (Randy) — persistent gallery memory for Calendar Pings. Same
  // pattern as the Reward store: dismissed questions stay X'd across page
  // reloads, and AI-generated pings from previous "Suggest more" taps stick
  // around instead of vanishing when the modal closes. Keyed per silo.
  const PING_KEY_NORM = (s) => (s || "").toLowerCase().split(/\s+/).join(" ");
  const PING_DISMISS_KEY = `mysilo_ping_dismiss_${siloId}`;
  const PING_GEN_KEY = `mysilo_ping_gen_${siloId}`;
  const readPingJson = (key, fallback) => {
    try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v == null ? fallback : v; }
    catch { return fallback; }
  };
  const writePingJson = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ } };
  const [pingDismissed, setPingDismissed] = useState(() => new Set(readPingJson(PING_DISMISS_KEY, [])));
  const [pingGenPersisted, setPingGenPersisted] = useState(() => readPingJson(PING_GEN_KEY, []));
  // Feb 20, 2026 (pt89T — Randy) — Nudge input for the gallery. Blank ⇒
  // random / silo-context driven (old behavior). Non-blank ⇒ every AI
  // "Suggest more" batch is biased toward this arena. State lives on
  // the modal so it clears between opens (nudges are one-off session
  // moves, not persistent silo settings).
  const [pingNudge, setPingNudge] = useState("");
  // Feb 20, 2026 (pt89T — Randy) — track the LAST question picked from the
  // gallery for dismiss-on-successful-save. Cleared when the user changes
  // the question text manually (they're now typing their own, so the
  // "picked" association no longer applies).
  const [pickedGalleryQuestion, setPickedGalleryQuestion] = useState("");

  const openGallery = async () => {
    setGallery({ list: [], busy: true });
    try {
      const r = await api.get(`/calendar/ping-suggestions`);
      const staples = r.data.suggestions || [];
      // Merge staples + previously AI-generated, minus dismissed. Dedupe.
      const combined = [...pingGenPersisted, ...staples];
      const merged = []; const seen = new Set();
      for (const s of combined) {
        const k = PING_KEY_NORM(s.question);
        if (!k || seen.has(k) || pingDismissed.has(k)) continue;
        seen.add(k); merged.push(s);
      }
      setGallery({ list: merged, busy: false });
    } catch { setGallery(null); toast.error("Couldn't load suggestions."); }
  };
  // pt89T-cap (Feb 20, 2026, Randy) — hard cap the persisted ping-gallery
  // at 20 per silo. When at cap, "Suggest more" is blocked with a nudge
  // to delete some cards to make room. Cap enforcement is purely local
  // (mirrors the server-side chore cap and the emoji library cap).
  const PING_GALLERY_CAP = 20;
  const pingGalleryFull = (gallery?.list || []).length >= PING_GALLERY_CAP;
  const suggestMore = async () => {
    if (pingGalleryFull) {
      toast.error(`Ping gallery is full (${PING_GALLERY_CAP}/${PING_GALLERY_CAP}). Delete a card to make room, then try again.`);
      return;
    }
    setGallery((g) => ({ ...(g || { list: [] }), busy: true }));
    try {
      const already = (gallery?.list || []).map((s) => (s.question || "").trim()).filter(Boolean);
      const dismissedArr = Array.from(pingDismissed);
      // Feed the LLM both the visible list AND previously-dismissed items so
      // rejected questions never come back through the AI door.
      // pt89U (Feb 20, 2026, Randy) — background-job pattern. The backend
      // fires the LLM asynchronously and returns a job_id immediately, so
      // Cloudflare's ~10s edge timeout can't drop the request. We then
      // poll `/ping-suggestions/job/{id}` every 2s until done or 60s.
      const startResp = await api.post(`/calendar/${siloId}/ping-suggestions/generate`, {
        already_seen: [...already, ...dismissedArr],
        nudge: pingNudge.trim() || null,
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("No job id from server.");
      let suggestions = [];
      const pollStart = Date.now();
      const MAX_MS = 60_000;
      const INTERVAL_MS = 2_000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((res) => setTimeout(res, INTERVAL_MS));
        try {
          const pollResp = await api.get(`/calendar/ping-suggestions/job/${jobId}`);
          const st = pollResp.data?.status;
          if (st === "done") {
            suggestions = pollResp.data?.suggestions || [];
            break;
          }
          if (st === "error") {
            throw new Error(pollResp.data?.error || "Generation failed.");
          }
        } catch (pollErr) {
          if (pollErr.response?.status === 404) throw pollErr;
          // Transient network hiccup during polling — keep trying until timeout.
        }
        if (Date.now() - pollStart > MAX_MS) {
          throw new Error("Timed out waiting for suggestions. Try again.");
        }
      }
      const seen = new Set([...already, ...dismissedArr].map(PING_KEY_NORM));
      const fresh = suggestions.filter((s) => {
        const key = PING_KEY_NORM(s.question);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (fresh.length === 0) {
        toast("No new ideas this time — try tweaking your silo docs or existing pings.", { id: "sugg-none" });
      }
      // pt89T-cap — trim so the list never exceeds the cap even if the LLM
      // returns more than the current available room.
      const roomLeft = Math.max(0, PING_GALLERY_CAP - (gallery?.list || []).length);
      const kept = fresh.slice(0, roomLeft);
      const skipped = fresh.length - kept.length;
      if (skipped > 0) {
        toast(`${skipped} idea${skipped === 1 ? "" : "s"} skipped — gallery cap of ${PING_GALLERY_CAP} reached.`, { id: "ping-cap-skip" });
      }
      setGallery((g) => ({ list: [...kept, ...(g?.list || [])], busy: false }));
      // Persist so they come back on next open.
      setPingGenPersisted((cur) => {
        const next = [...kept, ...cur].slice(0, PING_GALLERY_CAP);
        writePingJson(PING_GEN_KEY, next);
        return next;
      });
    } catch (e) {
      setGallery((g) => ({ ...(g || { list: [] }), busy: false }));
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Couldn't generate suggestions.");
    }
  };
  const removeSuggestion = (idx) => {
    let removedName = "";
    setGallery((g) => {
      if (!g) return g;
      const target = (g.list || [])[idx];
      removedName = target?.question || "";
      return { ...g, list: (g.list || []).filter((_, i) => i !== idx) };
    });
    const k = PING_KEY_NORM(removedName);
    if (!k) return;
    setPingDismissed((cur) => { const n = new Set(cur); n.add(k); writePingJson(PING_DISMISS_KEY, Array.from(n)); return n; });
    // Also strip from the persisted AI list so we don't just re-filter it out
    // over and over on subsequent opens.
    setPingGenPersisted((cur) => {
      const trimmed = cur.filter((s) => PING_KEY_NORM(s.question) !== k);
      if (trimmed.length !== cur.length) writePingJson(PING_GEN_KEY, trimmed);
      return trimmed;
    });
  };
  const pickSuggestion = (s) => {
    setPingQ(s.question); setTitle(s.label || s.question.slice(0, 60));
    if (s.time) setTime(s.time);
    setDow(s.dow || []); setFreq((s.dow || []).length ? "weekly" : "daily");
    setGallery(null);
    // Feb 20, 2026 (pt89T fix — Randy) — DO NOT dismiss on pick. Previously
    // this line implicitly dismissed the picked idea, which meant that if
    // the user then hit Cancel on the pre-filled form, the ping vanished
    // from the reserved list forever. Dismissal must ONLY happen when the
    // user (a) explicitly X's the card out or (b) successfully saves the
    // ping. Save-time dismissal is handled inside submit() by consulting
    // `pickedGalleryQuestion`.
    setPickedGalleryQuestion(s.question || "");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        kind, title: title.trim(),
        ping_question: kind === "ping" ? pingQ.trim() : null,
        notes: notes.trim(),
        start_date: kind === "task" && !date ? null : date,
        start_time: time || null, end_time: endTime || null,
        all_day: !time,
        tz_offset_minutes: new Date().getTimezoneOffset(),
        recurrence: freq === "none" ? null : { freq, dow, until: until || null },
        reminders,
        assignee_user_id: assignee || null,
        assignee_name: assignee ? (members.find((m) => m.user_id === assignee)?.name || null) : null,
        notify_scope: notifyScope,
      };
      if (editing?.id) {
        await api.patch(`/calendar/events/${editing.id}`, body);
        toast.success("Saved.");
        // Feb 24, 2026 (Randy pt89c-xxii Phase 0) — after any edit, offer
        // to propagate the change to future same-name siblings. Skip on
        // Google-synced events (the endpoint returns count:0 for those).
        // Skip on pings for now — they aren't calendar-timed items.
        if (kind !== "ping" && !editing.gcal_event_id && editing.source !== "gcal") {
          setPriorEvent(editing);
          // Build the "newEvent" projection using our just-saved body values.
          setNewEvent({
            id: editing.id,
            title: body.title,
            start_date: body.start_date,
            start_time: body.start_time,
            end_time: body.end_time,
            assignee_user_id: body.assignee_user_id,
            assignee_name: body.assignee_name,
          });
          setPropagateOpen(true);
          onSaved(); // refresh calendar behind the propagate modal
          setBusy(false);
          return;
        }
      }
      else {
        const r = await api.post(`/calendar/${siloId}/events`, body);
        toast.success(r.data?.status === "pending" ? "Sent to the calendar owner for approval." : "Added to your calendar.");
      }
      // Feb 20, 2026 (pt89T — Randy) — successful ping-save dismisses the
      // gallery card the user originally picked (if any). Save-time only,
      // never on pick. Editing an existing ping never dismisses (there was
      // no gallery pick in that flow).
      if (!editing?.id && kind === "ping" && pickedGalleryQuestion) {
        const k = PING_KEY_NORM(pickedGalleryQuestion);
        if (k) {
          setPingDismissed((cur) => { const n = new Set(cur); n.add(k); writePingJson(PING_DISMISS_KEY, Array.from(n)); return n; });
          setPingGenPersisted((cur) => {
            const trimmed = cur.filter((s) => PING_KEY_NORM(s.question) !== k);
            if (trimmed.length !== cur.length) writePingJson(PING_GEN_KEY, trimmed);
            return trimmed;
          });
        }
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this item (and all its future occurrences)?")) return;
    try { await api.delete(`/calendar/events/${editing.id}`); toast.success("Deleted."); onSaved(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <>
    {propagateOpen && priorEvent && newEvent && (
      <SiblingPropagateModal
        siloId={siloId}
        eventId={editing?.id}
        priorEvent={priorEvent}
        newEvent={newEvent}
        onClose={() => { setPropagateOpen(false); onClose(); }}
        onApplied={(_r) => { onSaved(); }}
      />
    )}
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="calendar-editor-modal">
      <div className="relative bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg">{editing ? "Edit" : "New"} calendar item</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="calendar-editor-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm flex-1 overflow-y-auto">
          <div className="flex gap-1.5">
            {Object.entries(KIND_META).map(([k, m]) => (
              <button key={k} type="button" onClick={() => setKind(k)} data-testid={`calendar-kind-${k}`}
                className={`flex-1 py-1.5 text-[11px] rounded border ${kind === k ? "text-white" : "bg-white hairline text-[#6B6A68]"}`}
                style={kind === k ? { background: m.dot, borderColor: m.dot } : {}}>
                {kindLabels[k] || m.label}
              </button>
            ))}
          </div>
          {kind === "ping" ? (
            <>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center justify-between">
                  The question your phone will ask you
                  <button type="button" onClick={openGallery} data-testid="ping-gallery-btn" className="underline text-[#4A5D4E] normal-case tracking-normal">Browse suggestions</button>
                </span>
                <textarea value={pingQ} onChange={(e) => setPingQ(e.target.value)} rows={2} data-testid="calendar-ping-question"
                  placeholder="e.g., What&rsquo;s on my and my family&rsquo;s agenda today?"
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Short name (shows on the grid)</span>
                <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="calendar-title-input"
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]" />
              </label>
            </>
          ) : (
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="calendar-title-input"
                className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]" />
            </label>
          )}

          {/* Feb 19, 2026 (Randy) — assignee picker hoisted to the TOP of
              the modal (right after Title) so it's the first meaningful
              decision the user makes.

              Feb 20, 2026 (pt89T refix — Randy): the picker MUST expose
              three distinct semantic groups so no user is ever confused:
                1. 🌐 Everyone  (value="", null assignee — shows on every feed)
                2. 🧍 Just me   (value=my user id — explicit self shortcut)
                3. One row per OTHER household member (owner+parents+kids),
                   skipping the current user (already covered by "Just me").
              Prior implementation showed only Everyone + the raw member
              rows, so an owner viewing their own dropdown perceived it as
              "Everybody + Just me" with no other members surfaced. */}
          {(() => {
            const me = getUser() || {};
            const myId = me.id || me.user_id || "";
            const others = (members || []).filter((m) => m.user_id && m.user_id !== myId);
            const meRow = (members || []).find((m) => m.user_id && m.user_id === myId);
            const showPicker = !!meRow || others.length > 0;
            if (!showPicker) return null;
            return (
              <label className="block" data-testid="event-edit-assignee-picker">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center justify-between">
                  {tAssignLabel}
                  <span className="normal-case tracking-normal text-[#8A8A88] italic">{tAssignEveryone} = shows on every household feed</span>
                </span>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="calendar-assignee-select"
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm">
                  <option value="">🌐 {tAssignEveryone}</option>
                  {meRow && (
                    <option value={meRow.user_id} data-testid="calendar-assignee-just-me">
                      🧍 {tAssignJustMe}{meRow.name ? ` — ${meRow.name}` : ""}
                    </option>
                  )}
                  {others.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.emoji || (m.is_kid ? "🙂" : "👤")} {m.name}
                      {m.is_kid ? ` (${tAssignKid})` : m.is_parent ? ` (${tAssignParent})` : m.is_owner ? ` (${tAssignOwner.toLowerCase()})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            );
          })()}

          {/* Feb 19, 2026 (Randy) — DATE / TIME / END row switched from
              plain flex to responsive grid. Prevents the middle TIME
              column from overlapping its own label + prevents the END
              column from being clipped past the modal edge on narrow
              widths. Two cols on phones (DATE + TIME, END wraps beneath);
              three cols on ≥sm widths. Each label has min-w-0 so long
              text doesn't break the layout. */}
          <div className={`grid gap-2 ${kind === "event" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
            <label className="min-w-0">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] block">Date{kind === "task" ? " (opt.)" : ""}</span>
              <input type="date" value={date || ""} onChange={(e) => setDate(e.target.value)} data-testid="calendar-date-input"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md bg-white text-sm" />
            </label>
            <label className="min-w-0">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] block">Time (empty=all-day)</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="calendar-time-input"
                className="mt-1 w-full px-2 py-2 border hairline rounded-md bg-white text-sm" />
            </label>
            {kind === "event" && (
              <label className="min-w-0 col-span-2 sm:col-span-1">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] block">End</span>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                  className="mt-1 w-full px-2 py-2 border hairline rounded-md bg-white text-sm" />
              </label>
            )}
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Repeats</span>
            <div className="flex gap-1.5 mt-1">
              {["none", "daily", "weekly", "monthly", "yearly"].map((f) => (
                <button key={f} type="button" onClick={() => setFreq(f)} data-testid={`calendar-freq-${f}`}
                  className={`flex-1 py-1.5 text-[10px] rounded capitalize ${freq === f ? "bg-[#4A5D4E] text-white" : "bg-white border hairline text-[#6B6A68]"}`}>
                  {f === "none" ? "Once" : f}
                </button>
              ))}
            </div>
            {freq === "weekly" && (
              <div className="flex gap-1 mt-1.5">
                {DOW_MIN.map((lbl, i) => (
                  <button key={i} type="button" onClick={() => toggleDow(i)} data-testid={`calendar-dow-${i}`}
                    className={`flex-1 py-1.5 text-xs rounded ${dow.includes(i) ? "bg-[#4A5D4E] text-white" : "bg-white border hairline text-[#6B6A68]"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            )}
            {freq !== "none" && (
              <label className="block mt-1.5">
                <span className="text-[10px] text-[#6B6A68]">Until (optional)</span>
                <input type="date" value={until || ""} onChange={(e) => setUntil(e.target.value)}
                  className="ml-2 px-2 py-1 border hairline rounded text-xs bg-white" />
              </label>
            )}
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Remind me (push to phone)</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {REMINDER_OPTS.map((o) => (
                <button key={o.v} type="button" onClick={() => toggleReminder(o.v)} data-testid={`calendar-reminder-${o.v}`}
                  className={`text-[10px] px-2 py-1 rounded ${reminders.includes(o.v) ? "bg-[#1C1B1A] text-white" : "bg-white border hairline text-[#6B6A68]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {kind === "ping" && <div className="text-[10px] text-[#6B6A68] italic mt-1">Pings always fire at their time — tapping the push opens the Q&amp;A with the question pre-asked.</div>}
          </div>
          {/* Feb 2026 (Randy) — notify-scope picker. The assignee itself is
              picked at the TOP of the modal (Assigned to). This picker
              controls WHOSE PHONE gets the push when the reminder fires:
              just the assignee, all parents, or everyone (household + any
              public subscribers with pings opted in, per Randy pt89c-xxi). */}
          {members.length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Push this reminder to</span>
              <div className="flex rounded-md border hairline overflow-hidden mt-1">
                {[
                  { v: "me", l: assignee ? "Assignee only" : "Only me" },
                  ...(hasParents ? [{ v: "parents", l: "All parents" }] : []),
                  { v: "everyone", l: "Everyone" },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => setNotifyScope(o.v)} data-testid={`calendar-notify-${o.v}`}
                    className={`flex-1 text-[11px] px-2 py-2 ${notifyScope === o.v ? "bg-[#1C1B1A] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Notes (optional)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white focus:outline-none focus:border-[#4A5D4E]" />
          </label>
        </div>
        <div className="px-5 py-3 border-t hairline flex items-center gap-2 shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          {editing?.id && (
            <button onClick={remove} data-testid="calendar-editor-delete" className="text-xs text-[#C16E5A] hover:underline flex items-center gap-1"><Trash size={13} /> Delete</button>
          )}
          <span className="flex-1" />
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={submit} disabled={busy || (kind === "ping" ? !pingQ.trim() : !title.trim())} data-testid="calendar-editor-save"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
            {busy ? "Saving…" : editing ? "Save changes" : "Add"}
          </button>
        </div>

        {gallery && (
          <div className="absolute inset-0 bg-[#FDFCF8] rounded-2xl overflow-y-auto" data-testid="ping-suggestions-gallery">
            <div className="px-5 py-3 border-b hairline flex items-center justify-between sticky top-0 bg-[#FDFCF8]">
              <h2 className="font-serif-display text-lg flex items-center gap-2"><Sparkle size={16} weight="duotone" className="text-[#C19A4A]" /> Ping suggestions</h2>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[#6B6A68] uppercase tracking-[0.14em]" data-testid="ping-gallery-count">
                  {(gallery.list || []).length}/{PING_GALLERY_CAP}
                </span>
                <button onClick={suggestMore} disabled={gallery.busy || pingGalleryFull} data-testid="ping-suggest-more"
                  title={pingGalleryFull ? `Gallery full (${PING_GALLERY_CAP}/${PING_GALLERY_CAP}). Delete a card to make room.` : "Generate more ping suggestions (uses your key)"}
                  className="text-[10px] px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50 flex items-center gap-1">
                  {gallery.busy ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} />} Suggest more (uses your key)
                </button>
                <button onClick={() => setGallery(null)} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="ping-gallery-close"><X size={16} /></button>
              </div>
            </div>
            {/* Feb 20, 2026 (pt89T — Randy) — Nudge input. One line at the
                top of the gallery. Blank ⇒ purely random/silo-context
                driven (previous behavior). Type "morning", "welding shop",
                "kids soccer practice" and every "Suggest more" batch will
                bias into that arena. Placed above the cards so its role
                is obvious even before the first tap. */}
            <div className="px-5 pt-3 pb-2 border-b hairline bg-[#FBFAF6] sticky top-[52px] z-[1]">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
                  <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Nudge
                  <span className="normal-case tracking-normal text-[#8A8A88] italic ml-auto">optional — biases every &ldquo;Suggest more&rdquo;</span>
                </span>
                <input type="text" value={pingNudge}
                  onChange={(e) => setPingNudge(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !gallery.busy) { e.preventDefault(); suggestMore(); } }}
                  data-testid="ping-suggest-nudge"
                  placeholder="e.g. morning, welding shop, kids at soccer, dog training"
                  maxLength={120}
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
              </label>
            </div>
            <div className="p-4 space-y-2">
              {gallery.list.map((s, i) => (
                <div key={i} className="relative group">
                  <button onClick={() => pickSuggestion(s)} data-testid={`ping-suggestion-${s.id || i}`}
                    className="w-full text-left p-3 pr-9 rounded-md border hairline hover:border-[#4A5D4E] hover:bg-[#F0F1EE]/50 transition-colors">
                    <div className="text-xs font-medium text-[#1C1B1A]">{s.label || "Suggestion"}</div>
                    <div className="text-[11px] text-[#6B6A68] mt-0.5 leading-relaxed">{s.question}</div>
                    <div className="text-[9px] text-[#4A5D4E] mt-1">{(s.dow || []).length ? (s.dow || []).map((d) => DOW_MIN[d]).join("·") : "daily"} · {fmt12(s.time) || "morning"}</div>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeSuggestion(i); }}
                    data-testid={`ping-suggestion-remove-${s.id || i}`}
                    title="Remove this idea (Suggest more won't repeat it in this session)"
                    className="absolute top-1.5 right-1.5 p-1 rounded-full text-[#B5B3AE] hover:text-[#C16E5A] hover:bg-white transition-colors">
                    <X size={13} weight="bold" />
                  </button>
                </div>
              ))}
              {gallery.list.length === 0 && !gallery.busy && <div className="text-xs text-[#6B6A68] italic">No suggestions loaded &mdash; hit &ldquo;Suggest more&rdquo; for fresh AI ideas.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
